/**
 * MuhurtaInfoTable Component
 * Displays D30 Muhurta system for time divisions
 * 30 divisions: 30 Muhurtas (parts of the day)
 */

import React from 'react';
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Text,
  HStack
} from '@chakra-ui/react';
import { JatakaResponse } from '../../../../../models/api';
import {
  calculateMuhurtas,
  MuhurtaData,
  MuhurtaAttributes,
  MUHURTA_DESCRIPTIONS,
  getMuhurtaType
} from '../data/MuhurtaTableData';
import { BLACK_ONLY_ASHTAMSHA_COLORS, getAshtamshaPlanetColor } from '../../../../../config/ashtamshaColors';
import { createPlanetDisplay } from '../helpers/planetDisplayHelper';
import {
  TABLE_WRAPPER_INLINE_STYLES,
  TABLE_CONTAINER_STYLES,
  TABLE_BASE_STYLES,
  TABLE_HEADER_ALT_STYLES,
  TABLE_ROW_HOVER_STYLES,
  TABLE_TD_BASE_STYLES,
  TABLE_TD_SMALL_STYLES,
  TABLE_TD_CENTER_STYLES,
  PLANET_TEXT_STYLES,
  EMPTY_MESSAGE_BOX_STYLES,
  EMPTY_CELL_TEXT_STYLES,
  COLUMN_STYLES
} from './styles/tableStyles';

export interface MuhurtaInfoTableProps {
  apiResponse?: JatakaResponse | null;
}

/**
 * Group divisions by Muhurta number for display
 */
interface MuhurtaRow {
  muhurtaNumber: number;
  muhurtaType: any;
  planets: MuhurtaAttributes[];
}

/**
 * Create all 30 Muhurta rows with planets grouped
 */
function groupByMuhurtaNumber(divisions: MuhurtaAttributes[]): MuhurtaRow[] {
  const rows: MuhurtaRow[] = [];
  const planetsByMuhurta: Record<number, MuhurtaAttributes[]> = {};

  // Initialize all 30 Muhurtas
  for (let i = 1; i <= 30; i++) {
    planetsByMuhurta[i] = [];
  }

  // Group planets by Muhurta number
  divisions.forEach(div => {
    planetsByMuhurta[div.muhurtaNumber].push(div);
  });

  // Create rows with all info
  for (let i = 1; i <= 30; i++) {
    const planetsInMuhurta = planetsByMuhurta[i];
    const muhurtaType = getMuhurtaType(i);

    rows.push({
      muhurtaNumber: i,
      muhurtaType,
      planets: planetsInMuhurta
    });
  }

  return rows;
}

export const MuhurtaInfoTable: React.FC<MuhurtaInfoTableProps> = ({ apiResponse }) => {
  if (!apiResponse) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>No chart data available</Text>
      </Box>
    );
  }

  // Get current hora planet for highlighting
  const currentHoraPlanet = apiResponse.detailed_panchanga?.hora_details?.current_planet;

  // Calculate Muhurta divisions
  const muhurtaData: MuhurtaData = calculateMuhurtas(apiResponse);

  if (!muhurtaData.divisions.length) {
    return (
      <Box {...EMPTY_MESSAGE_BOX_STYLES}>
        <Text>Unable to calculate Muhurta divisions</Text>
      </Box>
    );
  }

  // Group all divisions by Muhurta number (1-30)
  const muhurtaRows = groupByMuhurtaNumber(muhurtaData.divisions);

  return (
    <Box {...TABLE_WRAPPER_INLINE_STYLES}>
      {/* Muhurta Table */}
      <TableContainer {...TABLE_CONTAINER_STYLES}>
        <Table {...TABLE_BASE_STYLES}>
          <Thead {...TABLE_HEADER_ALT_STYLES}>
            <Tr>
              <Th {...COLUMN_STYLES.large}>Planets</Th>
              <Th {...COLUMN_STYLES.xsmall}>Part</Th>
              <Th {...COLUMN_STYLES.medium}>MUHURTA</Th>
              <Th {...COLUMN_STYLES.xxlarge}>Sphere of Influence</Th>
            </Tr>
          </Thead>
          <Tbody>
            {muhurtaRows.map((row) => {
              const muhurtaInfo = MUHURTA_DESCRIPTIONS[row.muhurtaType as keyof typeof MUHURTA_DESCRIPTIONS];

              return (
                <Tr key={row.muhurtaNumber} _hover={TABLE_ROW_HOVER_STYLES}>
                  {/* Planets column */}
                  <Td {...TABLE_TD_BASE_STYLES} minW="80px">
                    {row.planets.length > 0 ? (
                      <HStack spacing={1} flexWrap="wrap">
                        {row.planets.map((division, index) => {
                          // Use centralized planet display helper (DRY)
                          const planetDisplay = createPlanetDisplay(division.planet, currentHoraPlanet);
                          const colors = getAshtamshaPlanetColor(planetDisplay.planetType, BLACK_ONLY_ASHTAMSHA_COLORS);

                          return (
                            <Text
                              key={`${division.planet.short_name}-${index}`}
                              {...PLANET_TEXT_STYLES}
                              color={colors.color}
                              bg={colors.backgroundColor}
                              border={colors.borderColor ? `1px solid` : 'none'}
                              borderColor={colors.borderColor}
                            >
                              {planetDisplay.displayText}
                            </Text>
                          );
                        })}
                      </HStack>
                    ) : (
                      <Text {...EMPTY_CELL_TEXT_STYLES}></Text>
                    )}
                  </Td>

                  {/* Part Number */}
                  <Td {...TABLE_TD_CENTER_STYLES} fontWeight="normal">
                    <Text fontSize="xs">
                      {row.muhurtaNumber}
                    </Text>
                  </Td>

                  {/* MUHURTA */}
                  <Td {...TABLE_TD_SMALL_STYLES}>
                    <Text
                      fontSize="xs"
                      fontWeight="medium"
                    >
                      {muhurtaInfo.transliteration}
                    </Text>
                  </Td>

                  {/* Sphere of Influence */}
                  <Td {...TABLE_TD_SMALL_STYLES} fontWeight="normal">
                    <Text fontSize="xs" color="gray.700">
                      {muhurtaInfo.sphere}
                    </Text>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </TableContainer>
    </Box>
  );
};
